import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import { motion } from 'framer-motion'

import useGetDatasets from '../../containers/organizations/useGetDatasets'
import ScreenLoading from '../../components/ScreenLoading'
import mayaLogo from 'images/maya-logo.png'
import usePageTitle from 'containers/pageTitle/usePageTitle'

import './DatasetSelect.scss'

const DatasetSelect = () => {
  const { datasets, getDatasets, loadingDatasets, datasetsError } = useGetDatasets()
  const [filteredDatasets, setFilteredDatasets] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDatasetId, setSelectedDatasetId] = useState(null)
  
  const location = useLocation()
  const history = useHistory()
  const [cookies] = useCookies(['user_id'])

  usePageTitle('Select Dataset - Maya')

  const getData = async () => {
    try {
      const searchParams = new URLSearchParams(location.search)
      const orgId = searchParams.get('org_id')
      await getDatasets(orgId)
    } catch (error) {
      console.error('Error fetching datasets:', error)
    }
  }

  useEffect(() => {
    getData()
  }, [])

  useEffect(() => {
    if (datasets) {
      const filtered = datasets.filter(dataset =>
        dataset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (dataset.description && dataset.description.toLowerCase().includes(searchQuery.toLowerCase()))
      )
      setFilteredDatasets(filtered)
    }
  }, [datasets, searchQuery])

  if (loadingDatasets) {
    return <ScreenLoading />
  }

  if (datasetsError) {
    return (
      <div className="page-column">
        <header>
          <div className="logo">
            <img src={mayaLogo} alt="Maya" />
          </div>
        </header>
        <div className="container-grow">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="container-column dataset-select-box"
          >
            <div className="title-container">
              <h1 className="text-align-center">Error Loading Datasets</h1>
              <div className="secondary-text text-align-center">
                {datasetsError}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  if (!datasets || datasets.length === 0) {
    return (
      <div className="page-column">
        <header>
          <div className="logo">
            <img src={mayaLogo} alt="Maya" />
          </div>
        </header>
        <div className="container-grow">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="container-column dataset-select-box"
          >
            <div className="title-container">
              <h1 className="text-align-center">Preparing your workspace</h1>
              <div className="secondary-text text-align-center">
                Your workspace is still being prepared — this usually takes a few minutes.
                Please try again shortly.
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  const handleDatasetClick = (datasetId) => {
    if (selectedDatasetId === datasetId) {
      setSelectedDatasetId(null)
    } else {
      setSelectedDatasetId(datasetId)
    }
  }

  const handleAuthorize = () => {
    if (!selectedDatasetId) return

    const searchParams = new URLSearchParams(location.search)
    const mcpAuthToken = searchParams.get('mcp_auth_token')
    const nextUrl = searchParams.get('next')
    const orgId = searchParams.get('org_id')

    if (mcpAuthToken && nextUrl) {
      try {
        const redirectUrl = new URL(nextUrl, window.location.origin)
        if (!redirectUrl.searchParams.get('mcp_auth_token')) {
          redirectUrl.searchParams.append('mcp_auth_token', mcpAuthToken)
          redirectUrl.searchParams.append('user_id', cookies.user_id)
          if (orgId) {
            redirectUrl.searchParams.append('org_id', orgId)
          }
          redirectUrl.searchParams.append('dataset_id', selectedDatasetId)
        }
        window.location.href = redirectUrl.toString()
      } catch (error) {
        const url = new URL(nextUrl, window.location.origin)
        if (orgId) {
          url.searchParams.append('org_id', orgId)
        }
        url.searchParams.append('dataset_id', selectedDatasetId)
        window.location.assign(url.toString())
      }
    }
  }

  return (
    <div className="page-column">
      <header>
        <div className="logo">
          <img src={mayaLogo} alt="Maya" />
        </div>
      </header>
      <div className="container-grow">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="container-column dataset-select-box"
        >
          <div className="title-container">
            <h1 className="text-align-center">Select Dataset</h1>
            <div className="secondary-text text-align-center">
              Choose the dataset you want to authorize access for
            </div>
          </div>

          <div className="search-container" style={{marginBottom: "1.5rem"}}>
            <input
              type="text"
              placeholder="Search datasets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="datasets-list">
            {filteredDatasets.map(dataset => (
              <div
                key={dataset.id}
                className={`dataset-item ${selectedDatasetId === dataset.id ? 'selected' : ''}`}
                onClick={() => handleDatasetClick(dataset.id)}
              >
                <div className="dataset-name">{dataset.name}</div>
                {dataset.description && (
                  <div className="dataset-description">{dataset.description}</div>
                )}
                <div className="dataset-id">ID: {dataset.id}</div>
              </div>
            ))}
          </div>

          <div className="authorize-container">
            <button
              className={`submit-button ${!selectedDatasetId ? 'disabled' : ''}`}
              disabled={!selectedDatasetId}
              onClick={handleAuthorize}
            >
              Authorize Dataset
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default React.memo(DatasetSelect)